/*───────────────────────────────────────────────────────────────
 📄 File: AboutModal.tsx
 🧩 Description:
    About modal component displaying application branding,
    versioning (injected dynamically), system stack metrics,
    and licensing information. Displays "N/A" for any unprovided values.
────────────────────────────────────────────────────────────────*/

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, X, ShieldCheck, Cpu, Database, Server, Terminal, HardDrive, Calendar, Tag } from 'lucide-react';
import { useBranding } from '../../hooks/useBranding';
import { useI18n } from '../../hooks/useI18n';
import { useLicenseStatus } from '../../modules/licensing/hooks/useLicenseStatus';

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SystemInfo {
  distro?: string;
  node?: string;
  postgresql?: string;
  freeswitch?: string;
  nginx?: string;
  fail2ban?: string;
  nftables?: string;
  uptime?: number;
}

export function AboutModal({ isOpen, onClose }: AboutModalProps) {
  const { brandingData } = useBranding();
  const branding = brandingData?.brandingConfig;
  const { status: licenseStatus } = useLicenseStatus();
  const { t } = useI18n();

  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);

  const appName = branding?.appName || 'N/A';
  const companyName = (branding as any)?.companyName || 'N/A';
  const logoUrl = branding?.logoDark || branding?.logo;
  
  const version = import.meta.env.VITE_APP_VERSION || 'N/A';
  const releaseStage = import.meta.env.VITE_APP_RELEASE_STAGE || 'N/A';
  const buildDate = import.meta.env.VITE_APP_BUILD_DATE || 'N/A';

  const licenseType = licenseStatus
    ? (licenseStatus.mode === 'licensed'
        ? (licenseStatus.expires_at
            ? `Licensed (Expires ${new Date(licenseStatus.expires_at).toLocaleDateString()})`
            : 'Licensed (Perpetual)')
        : 'Community Edition')
    : 'N/A';

  useEffect(() => {
    if (!isOpen) return;

    const fetchSystemInfo = async () => {
      try {
        const protocol = window.location.protocol;
        const host = window.location.host;
        const res = await fetch(`${protocol}//${host}/monitoring-api/api/dashboard`);
        if (res.ok) {
          const json = await res.json();
          const sys = json.data?.system;
          if (sys) {
            setSystemInfo({
              distro: sys.distro,
              node: sys.versions?.node,
              postgresql: sys.services?.postgresql?.version,
              freeswitch: sys.services?.freeswitch?.version,
              nginx: sys.services?.nginx?.version,
              fail2ban: sys.services?.fail2ban?.version,
              nftables: sys.services?.nftables?.version,
              uptime: sys.uptime,
            });
          }
        }
      } catch (e) {
        console.warn('Failed to load system stats for About modal', e);
      }
    };

    fetchSystemInfo();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-lg overflow-hidden bg-card border border-border/60 rounded-xl shadow-2xl"
        >
          {/* Header Bar */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border/50 bg-muted/20">
            <div className="flex items-center gap-2.5">
              <Info className="w-5 h-5 text-primary" />
              <h2 className="text-base font-semibold text-foreground">
                {t('common:about', 'About')} {appName !== 'N/A' ? appName : ''}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted/80 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body Content */}
          <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
            {/* Branding Header Banner */}
            <div className="flex items-center gap-4 p-4 rounded-lg bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20">
              {logoUrl ? (
                <img src={logoUrl} alt={appName} className="h-12 max-w-[160px] object-contain" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center text-primary font-bold text-xl">
                  {appName !== 'N/A' ? appName.charAt(0) : 'A'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-bold text-foreground truncate">{appName}</h3>
                  {version !== 'N/A' && (
                    <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-primary/20 text-primary border border-primary/30">
                      v{version}
                    </span>
                  )}
                </div>
                {companyName !== 'N/A' && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {companyName}
                  </p>
                )}
              </div>
            </div>

            {/* Application Details */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 rounded-lg border border-border/50 bg-muted/10 space-y-1">
                <div className="flex items-center gap-1.5 text-muted-foreground font-medium">
                  <Tag className="w-3.5 h-3.5 text-primary" />
                  <span>{t('about:releaseStage', 'Release Stage')}</span>
                </div>
                <div className="font-semibold text-foreground">{releaseStage}</div>
              </div>

              <div className="p-3 rounded-lg border border-border/50 bg-muted/10 space-y-1">
                <div className="flex items-center gap-1.5 text-muted-foreground font-medium">
                  <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
                  <span>{t('about:license', 'License Type')}</span>
                </div>
                <div className="font-semibold text-foreground">{licenseType}</div>
              </div>

              <div className="p-3 rounded-lg border border-border/50 bg-muted/10 space-y-1">
                <div className="flex items-center gap-1.5 text-muted-foreground font-medium">
                  <Calendar className="w-3.5 h-3.5 text-blue-500" />
                  <span>{t('about:buildDate', 'Build Date')}</span>
                </div>
                <div className="font-mono text-foreground">{buildDate}</div>
              </div>

              <div className="p-3 rounded-lg border border-border/50 bg-muted/10 space-y-1">
                <div className="flex items-center gap-1.5 text-muted-foreground font-medium">
                  <Server className="w-3.5 h-3.5 text-purple-500" />
                  <span>{t('about:environment', 'Environment')}</span>
                </div>
                <div className="font-semibold text-foreground">
                  {systemInfo?.distro ? systemInfo.distro : 'N/A'}
                </div>
              </div>
            </div>

            {/* Subsystem & Live Infrastructure Info */}
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {t('about:subsystems', 'System & Subsystems Stack')}
              </h4>
              <div className="divide-y divide-border/40 rounded-lg border border-border/50 bg-muted/10 text-xs">
                <div className="flex items-center justify-between p-2.5">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <HardDrive className="w-3.5 h-3.5 text-primary" /> OS / Kernel
                  </span>
                  <span className="font-mono font-medium text-foreground">
                    {systemInfo?.distro || 'N/A'}
                  </span>
                </div>

                <div className="flex items-center justify-between p-2.5">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Cpu className="w-3.5 h-3.5 text-emerald-500" /> Node.js Runtime
                  </span>
                  <span className="font-mono font-medium text-foreground">
                    {systemInfo?.node ? `v${systemInfo.node}` : 'N/A'}
                  </span>
                </div>

                <div className="flex items-center justify-between p-2.5">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Database className="w-3.5 h-3.5 text-blue-500" /> PostgreSQL Database
                  </span>
                  <span className="font-mono font-medium text-foreground">
                    {systemInfo?.postgresql ? `v${systemInfo.postgresql}` : 'N/A'}
                  </span>
                </div>

                <div className="flex items-center justify-between p-2.5">
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Terminal className="w-3.5 h-3.5 text-purple-500" /> FreeSWITCH Engine
                  </span>
                  <span className="font-mono font-medium text-foreground">
                    {systemInfo?.freeswitch ? `v${systemInfo.freeswitch}` : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Copyright */}
          <div className="px-6 py-3 border-t border-border/50 bg-muted/20 flex items-center justify-between text-xs text-muted-foreground">
            <span>© {new Date().getFullYear()} {companyName !== 'N/A' ? companyName : appName !== 'N/A' ? appName : ''}</span>
            <button
              onClick={onClose}
              className="px-4 py-1.5 font-medium text-foreground bg-muted hover:bg-muted/80 rounded-md transition-colors"
            >
              {t('common:close', 'Close')}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
